import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { ArrowLeft, Eye, EyeOff, Loader2, Plus, Shield, X } from 'lucide-react';
import AppBar from 'components/AppBar';
import { DataTable } from 'components/data-table/DataTable';
import { Button } from 'components/ui/button';
import { Checkbox } from 'components/ui/checkbox';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import { Skeleton } from 'components/ui/skeleton';
import { useAuthSession } from '../contexts/AuthSessionContext';
import { isPlatformSuperAdmin } from '../services/authService';
import {
  APP_USER_ROLE_OPTIONS,
  createAppUser,
  listAppUsers,
  updateAppUser,
} from '../services/appUsersService';
import { createAppUserAccessColumns } from './team/accessColumns';
import './Team.css';

const LOT_SCOPE_OPTIONS = [
  { value: '', label: 'Not scoped' },
  { value: 'LOT 10 (Manyara)', label: 'LOT 10 (Manyara)' },
  { value: 'LOT 8 (Morogoro)', label: 'LOT 8 (Morogoro)' },
];

const EMPTY_USER_FORM = {
  email: '',
  name: '',
  role: 'admin',
  pin: '',
  driverKey: '',
  active: true,
  polesLot10: false,
  fuelAccess: false,
  toolsAccess: false,
  readOnly: false,
  lotScope: '',
};

function TeamUserAccess() {
  const session = useAuthSession();
  const isSuperAdmin = isPlatformSuperAdmin(session);

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState(EMPTY_USER_FORM);
  /** When editing, default visible so super-admin can read stored PIN. */
  const [pinVisible, setPinVisible] = useState(false);

  const loadUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const rows = await listAppUsers();
      setUsers(rows);
    } catch (err) {
      console.error(err);
      setError('Failed to load login users. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isSuperAdmin) return;
    loadUsers();
  }, [isSuperAdmin, loadUsers]);

  const handleOpenAdd = () => {
    setIsEditing(false);
    setEditingUser(null);
    setFormData(EMPTY_USER_FORM);
    setPinVisible(false);
    setShowModal(true);
  };

  const handleOpenEdit = useCallback((user) => {
    setIsEditing(true);
    setEditingUser(user);
    setPinVisible(true);
    setFormData({
      email: user.email,
      name: user.name,
      role: user.role,
      pin: user.pin,
      driverKey: user.driverKey || '',
      active: user.active !== false,
      polesLot10: user.polesLot10,
      fuelAccess: user.fuelAccess,
      toolsAccess: user.toolsAccess,
      readOnly: user.readOnly,
      lotScope: user.lotScope || '',
    });
    setShowModal(true);
  }, []);

  const handleCloseModal = () => {
    setShowModal(false);
    setIsEditing(false);
    setEditingUser(null);
    setFormData(EMPTY_USER_FORM);
    setPinVisible(false);
  };

  const appwritePasswordHint = useMemo(() => {
    const p = String(formData.pin || '').trim();
    if (!p) return '';
    return `${p}${p}`;
  }, [formData.pin]);

  const handleFormChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      if (isEditing && editingUser) {
        await updateAppUser(editingUser.id, formData);
      } else {
        await createAppUser(formData);
      }
      await loadUsers();
      handleCloseModal();
    } catch (err) {
      console.error(err);
      setError(String(err?.message || 'Failed to save user. Please try again.'));
    } finally {
      setSaving(false);
    }
  };

  const columns = useMemo(
    () => createAppUserAccessColumns({ onEdit: handleOpenEdit }),
    [handleOpenEdit]
  );

  const showDriverExtras = formData.role === 'driver';

  if (!isSuperAdmin) {
    return <Navigate to="/team" replace />;
  }

  return (
    <div className="team-page">
      <div className="team-scroll">
        <div className="team-panel">
          <AppBar
            variant="inset"
            breadcrumbs={[
              { label: 'Platform', hideOnMobile: true },
              { label: 'Team', to: '/team' },
              { label: 'User access' },
            ]}
          />

          <section className="team-intro" aria-label="User access overview">
            <div className="team-intro-text">
              <h1 className="team-intro-title team-intro-title--with-icon">
                <Shield className="h-6 w-6 text-primary" aria-hidden />
                Login users
              </h1>
              <p className="team-intro-subtitle">
                Manage PINs, roles, and what each person can see after sign-in. Only visible to
                you. After changing a PIN, run{' '}
                <code className="team-inline-code">npm run sync:auth-from-app-users</code> on a
                machine with <code className="team-inline-code">APPWRITE_API_KEY</code> so Appwrite
                Auth matches.
              </p>
            </div>
            <div className="team-intro-actions">
              <Button type="button" variant="outline" className="team-action-btn" asChild>
                <Link to="/team">
                  <ArrowLeft className="h-4 w-4" />
                  Team directory
                </Link>
              </Button>
              <Button type="button" className="team-action-btn" onClick={handleOpenAdd}>
                <Plus className="h-4 w-4" />
                Add login user
              </Button>
            </div>
          </section>

          {error ? (
            <div className="team-error" role="alert">
              {error}
            </div>
          ) : null}

          <div className="team-table-section">
            {loading ? (
              <div className="p-6">
                <Skeleton className="h-9 w-full max-w-sm" />
                <Skeleton className="mt-4 h-64 w-full" />
              </div>
            ) : (
              <DataTable
                columns={columns}
                data={users}
                searchPlaceholder="Search name, email, or role…"
              />
            )}
          </div>
        </div>
      </div>

      {showModal ? (
        <div className="team-modal-overlay" onClick={handleCloseModal}>
          <div
            className="team-modal team-modal--wide"
            role="dialog"
            aria-modal="true"
            aria-labelledby="user-access-modal-title"
            onClick={(ev) => ev.stopPropagation()}
          >
            <div className="team-modal__header">
              <h2 id="user-access-modal-title">
                {isEditing ? 'Edit login user' : 'Add login user'}
              </h2>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="team-modal__close team-soft-btn"
                onClick={handleCloseModal}
                aria-label="Close"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
            <form onSubmit={handleSubmit} className="team-modal__form">
              <div className="team-form-grid">
                <div className="team-field">
                  <Label htmlFor="ua-email">Email</Label>
                  <Input
                    id="ua-email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleFormChange('email', e.target.value)}
                    required
                    disabled={isEditing}
                    autoComplete="off"
                  />
                </div>
                <div className="team-field">
                  <Label htmlFor="ua-name">Display name</Label>
                  <Input
                    id="ua-name"
                    value={formData.name}
                    onChange={(e) => handleFormChange('name', e.target.value)}
                    required
                  />
                </div>
                <div className="team-field">
                  <Label htmlFor="ua-pin">PIN (users sign in with this PIN only)</Label>
                  <div className="team-pin-row">
                    <Input
                      id="ua-pin"
                      type={pinVisible ? 'text' : 'password'}
                      inputMode="numeric"
                      value={formData.pin}
                      onChange={(e) => handleFormChange('pin', e.target.value)}
                      required
                      minLength={4}
                      autoComplete="new-password"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      className="team-pin-toggle"
                      aria-label={pinVisible ? 'Hide PIN' : 'Show PIN'}
                      onClick={() => setPinVisible((v) => !v)}
                    >
                      {pinVisible ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </Button>
                  </div>
                  {appwritePasswordHint ? (
                    <p className="team-field-hint">
                      Stored in Appwrite as password:{' '}
                      <code className="team-pin-code">{appwritePasswordHint}</code>
                      {isEditing ? ' (shown when editing)' : null}
                    </p>
                  ) : null}
                </div>
                <div className="team-field">
                  <Label htmlFor="ua-role">Role</Label>
                  <select
                    id="ua-role"
                    className="team-select"
                    value={formData.role}
                    onChange={(e) => handleFormChange('role', e.target.value)}
                  >
                    {APP_USER_ROLE_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
                {showDriverExtras ? (
                  <div className="team-field">
                    <Label htmlFor="ua-driver-key">Driver key (fleet filter)</Label>
                    <Input
                      id="ua-driver-key"
                      value={formData.driverKey}
                      onChange={(e) => handleFormChange('driverKey', e.target.value)}
                      placeholder="e.g. geoffrey"
                    />
                  </div>
                ) : null}
              </div>

              <fieldset className="team-access-fieldset">
                <legend>What they can see</legend>
                {showDriverExtras ? (
                  <>
                    <label className="team-access-check">
                      <Checkbox
                        checked={formData.polesLot10}
                        onCheckedChange={(v) => handleFormChange('polesLot10', Boolean(v))}
                      />
                      <span>Poles delivery tracking (LOT 10)</span>
                    </label>
                    <label className="team-access-check">
                      <Checkbox
                        checked={formData.fuelAccess}
                        onCheckedChange={(v) => handleFormChange('fuelAccess', Boolean(v))}
                      />
                      <span>Fuel report</span>
                    </label>
                    <label className="team-access-check">
                      <Checkbox
                        checked={formData.toolsAccess}
                        onCheckedChange={(v) => handleFormChange('toolsAccess', Boolean(v))}
                      />
                      <span>Tools inventory</span>
                    </label>
                  </>
                ) : null}
                <label className="team-access-check">
                  <Checkbox
                    checked={formData.readOnly}
                    onCheckedChange={(v) => handleFormChange('readOnly', Boolean(v))}
                    disabled={formData.role === 'admin_lot10'}
                  />
                  <span>Read-only (no edits on allowed pages)</span>
                </label>
                <div className="team-field team-field--inline">
                  <Label htmlFor="ua-lot-scope">Data lot scope (optional)</Label>
                  <select
                    id="ua-lot-scope"
                    className="team-select"
                    value={formData.lotScope}
                    onChange={(e) => handleFormChange('lotScope', e.target.value)}
                  >
                    {LOT_SCOPE_OPTIONS.map((opt) => (
                      <option key={opt.value || 'none'} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
              </fieldset>

              <label className="team-access-check">
                <Checkbox
                  checked={formData.active}
                  onCheckedChange={(v) => handleFormChange('active', Boolean(v))}
                />
                <span>Account active (can sign in)</span>
              </label>

              <div className="team-modal__actions">
                <Button
                  type="button"
                  variant="ghost"
                  className="team-soft-btn"
                  onClick={handleCloseModal}
                  disabled={saving}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={saving}>
                  {saving ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Saving…
                    </>
                  ) : isEditing ? (
                    'Save changes'
                  ) : (
                    'Create user'
                  )}
                </Button>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default TeamUserAccess;
